'use client';

import React, { useState, useEffect } from 'react';
import {
  Layers,
  ShoppingBag,
  FileSpreadsheet,
  TrendingUp,
  Package,
  Users,
  CheckCircle,
  Clock,
  ArrowUpRight,
  Filter,
  Check,
  Eye,
  FileText,
  Globe,
  Truck,
  BarChart3,
  Plus,
  AlertTriangle,
  Tag,
  Settings,
  ShieldCheck,
  Building,
  MapPin,
  Calendar,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import { useCommerce } from '@/context/CommerceContext';
import type { Quotation, Order, Product } from '@/data/mockData';

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://elavarasiapi.orgininfo.com/api';

// Section 36 Order Types
const ORDER_TYPES = [
  { id: 'RETAIL', name: 'Retail Order', color: 'bg-emerald-100 text-emerald-800' },
  { id: 'B2B', name: 'B2B Wholesale', color: 'bg-blue-100 text-blue-800' },
  { id: 'BULK', name: 'Bulk Function', color: 'bg-purple-100 text-purple-800' },
  { id: 'CORPORATE', name: 'Corporate Gifting', color: 'bg-amber-100 text-amber-800' },
  { id: 'OCCASION', name: 'Occasion Hamper', color: 'bg-rose-100 text-rose-800' },
  { id: 'FESTIVAL', name: 'Festival Pre-Order', color: 'bg-red-100 text-red-800' },
  { id: 'CUSTOM_BOX', name: 'Custom Package', color: 'bg-indigo-100 text-indigo-800' },
  { id: 'EXPORT', name: 'Export Consignment', color: 'bg-orange-100 text-orange-800' },
];

export default function AdminPortal() {
  const { setActiveView, openInvoice, products, orders } = useCommerce();
  
  // Section 38 Recommended Dashboard Modules
  const [activeTab, setActiveTab] = useState<
    'overview' | 'quotations' | 'orders' | 'inventory' | 'products' | 'customers' | 'export' | 'logistics' | 'reports'
  >('overview');
  
  const [quotationsList, setQuotationsList] = useState<Quotation[]>([]);
  const [ordersList, setOrdersList] = useState<Order[]>(orders || []);
  const [productsList, setProductsList] = useState<Product[]>(products || []);
  const [selectedQuoteForEdit, setSelectedQuoteForEdit] = useState<Quotation | null>(null);
  const [orderChannelFilter, setOrderChannelFilter] = useState('ALL');

  React.useEffect(() => {
    if (products && products.length > 0) setProductsList(products);
  }, [products]);

  React.useEffect(() => {
    if (orders && orders.length > 0) setOrdersList(orders);
  }, [orders]);
  
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    activeOrders: 0,
    pendingQuotations: 0,
    b2bLeads: 0,
    exportLeads: 0,
    lowStockCount: 0,
  });

  // Fetch live stats & backend data
  useEffect(() => {
    fetch(`${API_BASE}/v1/admin/stats`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setStats(prev => ({
            ...prev,
            totalRevenue: data.data.totalRevenue || prev.totalRevenue,
            totalOrders: data.data.totalOrders || prev.totalOrders,
            pendingQuotations: data.data.pendingQuotations || prev.pendingQuotations,
          }));
        }
      })
      .catch(() => {});

    fetch(`${API_BASE}/v1/quotations`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data?.length) setQuotationsList(data.data);
      })
      .catch(() => {});

    fetch(`${API_BASE}/v1/orders`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data?.length) setOrdersList(data.data);
      })
      .catch(() => {});
  }, []);

  // Update Quotation Status
  const handleUpdateQuoteStatus = (id: string, newStatus: Quotation['status']) => {
    setQuotationsList(prev =>
      prev.map(q => q.id === id ? { ...q, status: newStatus } : q)
    );
    fetch(`${API_BASE}/v1/quotations/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus })
    }).catch(() => {});
  };

  // Convert Quotation to Order (Section 37)
  const handleConvertQuoteToOrder = async (quote: Quotation) => {
    try {
      const res = await fetch(`${API_BASE}/v1/quotations/${quote.id}/convert`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        handleUpdateQuoteStatus(quote.id, 'CONVERTED_TO_ORDER');
        if (data.data?.order) {
          setOrdersList(prev => [data.data.order, ...prev]);
        }
        alert(`Quotation ${quote.quotationNumber} successfully converted to Live Order in the Kitchen Fulfillment Pipeline!`);
      }
    } catch {
      handleUpdateQuoteStatus(quote.id, 'CONVERTED_TO_ORDER');
      alert(`Quotation ${quote.quotationNumber} marked converted to order.`);
    }
  };

  // Update Order Status (Section 36)
  const handleUpdateOrderStatus = (id: string, newStatus: Order['orderStatus']) => {
    setOrdersList(prev =>
      prev.map(o => o.id === id ? { ...o, orderStatus: newStatus } : o)
    );
    fetch(`${API_BASE}/v1/orders/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus })
    }).catch(() => {});
  };

  // Inventory Stock Adjustment (Section 40)
  const handleAdjustStock = (productId: string, variantSku: string, delta: number) => {
    setProductsList(prev =>
      prev.map(p => {
        if (p.id !== productId) return p;
        return {
          ...p,
          variants: p.variants.map(v =>
            v.sku === variantSku ? { ...v, stock: Math.max(0, v.stock + delta) } : v
          )
        };
      })
    );

    fetch(`${API_BASE}/v1/products/${productId}/stock`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variantSku, stockDelta: delta })
    }).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF] pb-20 text-[#171711]">
      
      {/* Admin Top Banner & Brand Bar */}
      <div className="bg-[#6F0A06] text-[#F7F4D6] px-4 lg:px-8 py-3.5 border-b-2 border-[#E09F55] shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-[#E09F55] text-[#171711] font-display font-black flex items-center justify-center text-xl shadow">
              E
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="font-display font-bold text-base sm:text-lg text-[#F2BC6F]">
                  Elavarasi Foods — Central Control Engine
                </h1>
                <span className="text-[10px] bg-[#387613] text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                  Live Operations
                </span>
              </div>
              <p className="text-[11px] text-[#DDDAC4]">
                Sections 31–52 Architecture • Omnichannel Fulfillment & Sales Pipeline
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-3">
            <span className="hidden sm:inline-block text-[11px] text-[#F2BC6F] bg-black/30 px-3 py-1 rounded-lg border border-[#E09F55]/30">
              Cutoff: 2:00 PM IST
            </span>
            <button
              onClick={() => setActiveView('store')}
              className="px-3.5 py-1.5 rounded-xl bg-[#E09F55] hover:bg-[#F2BC6F] text-[#171711] text-xs font-bold transition-all shadow cursor-pointer flex items-center space-x-1.5"
            >
              <span>← Back to Customer Store</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-6 space-y-6">
        
        {/* Module Navigation Tabs (Section 38 Recommended Modules) */}
        <div className="bg-white rounded-2xl border border-[#DDDAC4] p-2 shadow-sm flex items-center overflow-x-auto gap-1 no-scrollbar text-xs font-bold">
          {[
            { id: 'overview', label: 'Executive Dashboard', icon: TrendingUp },
            { id: 'quotations', label: `Sales & RFQs (${quotationsList.length})`, icon: FileSpreadsheet },
            { id: 'orders', label: `Order Engine (${ordersList.length})`, icon: ShoppingBag },
            { id: 'inventory', label: `Inventory & Stock (${productsList.length})`, icon: Package },
            { id: 'products', label: 'Products & Variants', icon: Tag },
            { id: 'customers', label: 'B2B & Customers', icon: Users },
            { id: 'export', label: 'Export Desk', icon: Globe },
            { id: 'logistics', label: 'Logistics & Lead Time', icon: Truck },
            { id: 'reports', label: 'Analytics & Reports', icon: BarChart3 },
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-3 py-2 rounded-xl flex items-center space-x-1.5 whitespace-nowrap transition-all cursor-pointer ${
                  isActive
                    ? 'bg-[#950E09] text-[#F7F4D6] shadow'
                    : 'text-[#171711]/70 hover:bg-[#F7F4D6] hover:text-[#950E09]'
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-[#F2BC6F]' : 'text-[#171711]/50'}`} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* ============================================================ */}
        {/* MODULE 1: EXECUTIVE DASHBOARD */}
        {/* ============================================================ */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            
            {/* KPI Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-5 rounded-2xl bg-white border border-[#DDDAC4] shadow-sm space-y-2">
                <div className="flex items-center justify-between text-[#171711]/60 text-xs font-semibold">
                  <span>Gross Commerce Revenue</span>
                  <TrendingUp className="w-4 h-4 text-[#387613]" />
                </div>
                <div className="font-display text-2xl font-black text-[#950E09]" suppressHydrationWarning>
                  ₹{stats.totalRevenue.toLocaleString('en-IN')}
                </div>
                <div className="text-[11px] text-[#387613] font-semibold flex items-center space-x-1">
                  <ArrowUpRight className="w-3.5 h-3.5" />
                  <span>+18.4% festive pre-order surge</span>
                </div>
              </div>

              <div className="p-5 rounded-2xl bg-white border border-[#DDDAC4] shadow-sm space-y-2">
                <div className="flex items-center justify-between text-[#171711]/60 text-xs font-semibold">
                  <span>Active Kitchen Orders</span>
                  <Package className="w-4 h-4 text-[#E09F55]" />
                </div>
                <div className="font-display text-2xl font-black text-[#171711]">
                  {ordersList.filter(o => o.orderStatus !== 'DELIVERED').length}
                </div>
                <div className="text-[11px] text-[#171711]/70">
                  {ordersList.filter(o => o.orderStatus === 'CONFIRMED').length} awaiting kitchen batch fry
                </div>
              </div>

              <div className="p-5 rounded-2xl bg-white border border-[#DDDAC4] shadow-sm space-y-2">
                <div className="flex items-center justify-between text-[#171711]/60 text-xs font-semibold">
                  <span>Pending B2B & RFQ Quotes</span>
                  <FileSpreadsheet className="w-4 h-4 text-[#950E09]" />
                </div>
                <div className="font-display text-2xl font-black text-[#950E09]">
                  {quotationsList.filter(q => q.status === 'PENDING_REVIEW').length}
                </div>
                <div className="text-[11px] text-[#950E09] font-semibold">
                  Requires sales price confirmation
                </div>
              </div>

              <div className="p-5 rounded-2xl bg-white border border-[#DDDAC4] shadow-sm space-y-2">
                <div className="flex items-center justify-between text-[#171711]/60 text-xs font-semibold">
                  <span>Global Export & B2B Leads</span>
                  <Globe className="w-4 h-4 text-[#387613]" />
                </div>
                <div className="font-display text-2xl font-black text-[#387613]">
                  {stats.b2bLeads} Verified Accounts
                </div>
                <div className="text-[11px] text-[#387613]">
                  USA, UK, UAE & Chennai branches
                </div>
              </div>
            </div>

            {/* Section 36: Order & Fulfillment Engine Channel Matrix */}
            <div className="p-6 rounded-2xl bg-white border border-[#DDDAC4] shadow-sm space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <h3 className="font-display font-bold text-base text-[#171711]">
                    Section 36: Order & Fulfillment Engine (8 Distinct Channels)
                  </h3>
                  <p className="text-xs text-[#171711]/60">
                    Omnichannel workflow routing orders to retail courier, commercial freight, or custom gifting packing tables.
                  </p>
                </div>
                <span className="text-xs bg-[#387613]/10 text-[#387613] font-bold px-3 py-1 rounded-full border border-[#387613]/20 self-start sm:self-auto">
                  100% Automated Routing
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {ORDER_TYPES.map(ot => {
                  const channelKey = ot.id === 'RETAIL' ? 'B2C_RETAIL' : ot.id === 'B2B' ? 'B2B_WHOLESALE' : ot.id;
                  const channelCount = ordersList.filter(o => o.channel === channelKey).length;
                  return (
                    <div key={ot.id} className="p-3.5 rounded-xl border border-[#DDDAC4] bg-[#F7F4D6]/30 flex flex-col justify-between">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full inline-block w-fit ${ot.color}`}>
                        {ot.name}
                      </span>
                      <div className="mt-2 flex items-baseline justify-between">
                        <span className="text-xs text-[#171711]/70">Active In Pipeline:</span>
                        <span className="font-display font-black text-lg text-[#171711]">{channelCount}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Section 33 & 34: Central Kitchen Logistics Status Banner */}
            <div className="p-5 rounded-2xl bg-gradient-to-r from-[#6F0A06] to-[#950E09] text-[#F7F4D6] shadow-md flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="space-y-1 max-w-2xl">
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-[#F2BC6F]" />
                  <span className="font-bold text-xs uppercase tracking-wider text-[#F2BC6F]">
                    Section 33 & 34: Daily Kitchen Cutoff Status
                  </span>
                </div>
                <h4 className="font-display font-bold text-lg">
                  Today’s Central Kitchen Batch Prep & Same-Day Dispatch Running
                </h4>
                <p className="text-xs text-[#DDDAC4]">
                  Cutoff at 2:00 PM IST. Orders verified before cutoff time are fresh fried in pure cow ghee and packed today. Custom boxes and corporate orders receive 24–48h production lead time.
                </p>
              </div>

              <button
                onClick={() => setActiveTab('orders')}
                className="px-4 py-2.5 rounded-xl bg-[#E09F55] hover:bg-[#F2BC6F] text-[#171711] font-bold text-xs shadow cursor-pointer whitespace-nowrap"
              >
                Inspect Kitchen Queue →
              </button>
            </div>

          </div>
        )}

        {/* ============================================================ */}
        {/* MODULE 2: SALES & QUOTATION MANAGEMENT (SECTION 37) */}
        {/* ============================================================ */}
        {activeTab === 'quotations' && (
          <div className="bg-white rounded-2xl border border-[#DDDAC4] shadow-sm overflow-hidden p-5 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="font-display font-bold text-base text-[#171711]">
                  Section 37: Sales & Quotation Management Pipeline
                </h3>
                <p className="text-xs text-[#171711]/60">
                  Review requirements → calculate pricing → add volume discounts & packaging → generate quote → approve & convert to order.
                </p>
              </div>

              <span className="text-xs bg-[#E09F55]/20 text-[#835926] font-bold px-3 py-1 rounded-full border border-[#E09F55]/30">
                {quotationsList.filter(q => q.status === 'PENDING_REVIEW').length} Awaiting Sales Review
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead>
                  <tr className="border-b border-[#DDDAC4] bg-[#F7F4D6]/50 text-[#171711]/80 font-bold uppercase tracking-wider text-[11px]">
                    <th className="p-3">RFQ Ref</th>
                    <th className="p-3">Client / Organization</th>
                    <th className="p-3">Channel</th>
                    <th className="p-3">Delivery City & Date</th>
                    <th className="p-3">Est. Amount</th>
                    <th className="p-3">Status</th>
                    <th className="p-3 text-right">Section 37 Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#DDDAC4]/60 text-[#171711]">
                  {quotationsList.map((q) => (
                    <tr key={q.id} className="hover:bg-[#F7F4D6]/20 transition-colors">
                      <td className="p-3 font-mono font-bold text-[#950E09]">
                        {q.quotationNumber}
                      </td>
                      <td className="p-3">
                        <div className="font-bold">{q.clientName}</div>
                        {q.companyName && <div className="text-[11px] text-[#171711]/60 font-medium">{q.companyName}</div>}
                        <div className="text-[10px] text-[#171711]/50">{q.phone} • {q.email}</div>
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#E09F55]/20 text-[#835926]">
                          {q.orderType}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="font-medium">{q.deliveryDateRequired || 'Flexible'}</div>
                        <div className="text-[10px] text-[#171711]/60">{q.destinationCity}</div>
                      </td>
                      <td className="p-3">
                        <span className="font-display font-black text-sm text-[#950E09]" suppressHydrationWarning>
                          ₹{q.totalAmount.toLocaleString('en-IN')}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          q.status === 'ACCEPTED'
                            ? 'bg-[#387613]/20 text-[#387613]'
                            : q.status === 'CONVERTED_TO_ORDER'
                            ? 'bg-purple-100 text-purple-800'
                            : q.status === 'QUOTATION_SENT'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}>
                          {q.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="p-3 text-right space-x-1.5">
                        {q.status === 'PENDING_REVIEW' && (
                          <button
                            onClick={() => handleUpdateQuoteStatus(q.id, 'QUOTATION_SENT')}
                            className="px-2.5 py-1 rounded-lg bg-[#387613] hover:bg-[#1F5B18] text-white text-[11px] font-bold cursor-pointer"
                          >
                            Send Quote
                          </button>
                        )}
                        {q.status === 'QUOTATION_SENT' && (
                          <button
                            onClick={() => handleUpdateQuoteStatus(q.id, 'ACCEPTED')}
                            className="px-2.5 py-1 rounded-lg bg-[#E09F55] hover:bg-[#F2BC6F] text-[#171711] text-[11px] font-bold cursor-pointer"
                          >
                            Client Accepted
                          </button>
                        )}
                        {q.status === 'ACCEPTED' && (
                          <button
                            onClick={() => handleConvertQuoteToOrder(q)}
                            className="px-3 py-1 rounded-lg bg-[#950E09] hover:bg-[#B90505] text-white text-[11px] font-bold cursor-pointer shadow flex items-center gap-1 inline-flex"
                          >
                            <span>Convert to Order</span>
                            <ArrowRight className="w-3 h-3 text-[#F2BC6F]" />
                          </button>
                        )}
                        {q.status === 'CONVERTED_TO_ORDER' && (
                          <span className="text-[11px] text-purple-700 font-bold">
                            ✓ In Kitchen Queue
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* MODULE 3: ORDER FULFILLMENT & MULTI-BRANCH (SECTIONS 35 & 36) */}
        {/* ============================================================ */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-2xl border border-[#DDDAC4] shadow-sm overflow-hidden p-5 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="font-display font-bold text-base text-[#171711]">
                  Section 36: Central Kitchen Fulfillment Pipeline
                </h3>
                <p className="text-xs text-[#171711]/60">
                  Track lifecycle from CONFIRMED → PACKING → DISPATCHED → DELIVERED with Section 35 multi-branch delivery splitting.
                </p>
              </div>

              {/* Order Channel Filter */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 font-semibold">Filter Channel:</span>
                <select
                  value={orderChannelFilter}
                  onChange={(e) => setOrderChannelFilter(e.target.value)}
                  className="p-1.5 rounded-lg border border-[#DDDAC4] text-xs font-semibold bg-white"
                >
                  <option value="ALL">All Channels</option>
                  <option value="B2C_RETAIL">B2C Retail</option>
                  <option value="BULK">Bulk / Occasion</option>
                  <option value="CORPORATE">Corporate</option>
                  <option value="EXPORT">Export</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead>
                  <tr className="border-b border-[#DDDAC4] bg-[#F7F4D6]/50 text-[#171711]/80 font-bold uppercase tracking-wider text-[11px]">
                    <th className="p-3">Order Ref</th>
                    <th className="p-3">Customer / Client</th>
                    <th className="p-3">Channel</th>
                    <th className="p-3">Items & Packaging</th>
                    <th className="p-3">Payment</th>
                    <th className="p-3">Fulfillment Status</th>
                    <th className="p-3 text-right">Advance Step / Invoice</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#DDDAC4]/60 text-[#171711]">
                  {ordersList
                    .filter(o => orderChannelFilter === 'ALL' || o.channel === orderChannelFilter)
                    .map((ord) => (
                    <tr key={ord.id} className="hover:bg-[#F7F4D6]/20 transition-colors">
                      <td className="p-3 font-mono font-bold text-[#950E09]">
                        {ord.orderNumber}
                      </td>
                      <td className="p-3">
                        <div className="font-bold">{ord.customerName}</div>
                        <div className="text-[10px] text-[#171711]/60">{ord.shippingAddress.city}, {ord.shippingAddress.state}</div>
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-800">
                          {ord.channel}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="font-medium">{ord.items.length} Product Lines</div>
                        <div className="text-[10px] text-gray-500 truncate max-w-xs">
                          {ord.items.map(i => `${i.name} (${i.quantity})`).join(', ')}
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="font-bold text-[#387613]">{ord.paymentMethod}</span>
                        <div className="font-display text-xs font-bold text-[#171711]">₹{ord.totalAmount}</div>
                      </td>
                      <td className="p-3">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          ord.orderStatus === 'DELIVERED'
                            ? 'bg-[#387613]/20 text-[#387613]'
                            : ord.orderStatus === 'DISPATCHED'
                            ? 'bg-blue-100 text-blue-800'
                            : ord.orderStatus === 'PACKING'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}>
                          {ord.orderStatus}
                        </span>
                      </td>
                      <td className="p-3 text-right space-x-1">
                        {ord.orderStatus === 'CONFIRMED' && (
                          <button
                            onClick={() => handleUpdateOrderStatus(ord.id, 'PACKING')}
                            className="px-2.5 py-1 rounded bg-[#E09F55] text-[#171711] font-bold text-[10px] cursor-pointer"
                          >
                            Mark Packing
                          </button>
                        )}
                        {ord.orderStatus === 'PACKING' && (
                          <button
                            onClick={() => handleUpdateOrderStatus(ord.id, 'DISPATCHED')}
                            className="px-2.5 py-1 rounded bg-blue-600 text-white font-bold text-[10px] cursor-pointer"
                          >
                            Mark Dispatched
                          </button>
                        )}
                        {ord.orderStatus === 'DISPATCHED' && (
                          <button
                            onClick={() => handleUpdateOrderStatus(ord.id, 'DELIVERED')}
                            className="px-2.5 py-1 rounded bg-[#387613] text-white font-bold text-[10px] cursor-pointer"
                          >
                            Mark Delivered
                          </button>
                        )}
                        <button
                          onClick={() => openInvoice(ord)}
                          className="px-2 py-1 rounded border border-[#DDDAC4] hover:bg-gray-100 text-[10px] font-semibold text-gray-700 cursor-pointer"
                          title="View Tax Invoice"
                        >
                          Tax Invoice
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Section 35: Corporate Multi-Location Distribution Card */}
            <div className="p-4 rounded-xl bg-[#F7F4D6]/60 border border-[#E09F55]/40 mt-4 space-y-2">
              <div className="flex items-center space-x-2 text-xs font-bold text-[#950E09]">
                <Building className="w-4 h-4 text-[#950E09]" />
                <span>Section 35: Corporate Multi-Location Order Distribution Capabilities</span>
              </div>
              <p className="text-xs text-[#171711]/70">
                Single corporate orders can be split and simultaneously dispatched across multiple branch locations with individual airway bills and separate consignee receipts.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs pt-1">
                <div className="p-2.5 rounded-lg bg-white border border-[#DDDAC4]">
                  <span className="font-bold block">Chennai Tech Park</span>
                  <span className="text-[11px] text-gray-500">100 Gift Hampers (Airway #AWB-CH-01)</span>
                </div>
                <div className="p-2.5 rounded-lg bg-white border border-[#DDDAC4]">
                  <span className="font-bold block">Coimbatore Office</span>
                  <span className="text-[11px] text-gray-500">50 Gift Hampers (Airway #AWB-CBE-04)</span>
                </div>
                <div className="p-2.5 rounded-lg bg-white border border-[#DDDAC4]">
                  <span className="font-bold block">Bangalore Electronics City</span>
                  <span className="text-[11px] text-gray-500">75 Gift Hampers (Airway #AWB-BLR-09)</span>
                </div>
                <div className="p-2.5 rounded-lg bg-white border border-[#DDDAC4]">
                  <span className="font-bold block">Kochi Infopark</span>
                  <span className="text-[11px] text-gray-500">25 Gift Hampers (Airway #AWB-KOC-12)</span>
                </div>
              </div>
            </div>

          </div>
        )}

        {/* ============================================================ */}
        {/* MODULE 4: INVENTORY MANAGEMENT & MOVEMENTS (SECTION 40) */}
        {/* ============================================================ */}
        {activeTab === 'inventory' && (
          <div className="bg-white rounded-2xl border border-[#DDDAC4] shadow-sm overflow-hidden p-5 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="font-display font-bold text-base text-[#171711]">
                  Section 40: Inventory Tracking & Stock Movements
                </h3>
                <p className="text-xs text-[#171711]/60">
                  Real-time variant-level inventory, low-stock alerts (&lt;50 units), and central kitchen batch replenishment.
                </p>
              </div>

              <div className="flex items-center space-x-2 text-xs bg-amber-50 text-amber-800 px-3 py-1 rounded-full border border-amber-200">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                <span>Low Stock Threshold: 50 units</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {productsList.map(p => (
                <div key={p.id} className="p-4 rounded-xl border border-[#DDDAC4] flex space-x-4 items-start bg-[#F7F4D6]/20">
                  <img src={p.image} alt={p.name} className="w-16 h-16 rounded-xl object-cover border border-[#DDDAC4]" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="font-display font-bold text-sm text-[#171711] truncate">{p.name}</div>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#387613]/15 text-[#387613] font-bold">
                        {p.category}
                      </span>
                    </div>
                    <div className="text-xs text-[#387613] font-medium">{p.tamilName} • Shelf Life: {p.shelfLife}</div>
                    
                    {/* Variants and stock adjustments */}
                    <div className="space-y-1.5 mt-2.5">
                      {p.variants.map(v => {
                        const isLow = v.stock < 50;
                        return (
                          <div key={v.sku} className="flex items-center justify-between p-2 rounded-lg bg-white border border-[#DDDAC4] text-xs">
                            <div>
                              <span className="font-bold">{v.weight}</span>
                              <span className="text-gray-500 ml-2">₹{v.price}</span>
                            </div>

                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                isLow ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'
                              }`}>
                                {v.stock} in stock {isLow && '⚠️ Low'}
                              </span>

                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => handleAdjustStock(p.id, v.sku, 25)}
                                  className="px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-800 text-[10px] font-bold cursor-pointer"
                                  title="Add 25 units from central kitchen batch"
                                >
                                  +25
                                </button>
                                <button
                                  onClick={() => handleAdjustStock(p.id, v.sku, 50)}
                                  className="px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-800 text-[10px] font-bold cursor-pointer"
                                  title="Add 50 units from central kitchen batch"
                                >
                                  +50
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* MODULE 5: PRODUCTS & VARIANTS (SECTION 39) */}
        {/* ============================================================ */}
        {activeTab === 'products' && (
          <div className="bg-white rounded-2xl border border-[#DDDAC4] shadow-sm overflow-hidden p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display font-bold text-base text-[#171711]">
                  Section 39: Product Catalog & Recipe Specifications
                </h3>
                <p className="text-xs text-[#171711]/60">
                  Manage variants, weight tiers (100g, 250g, 500g, 1kg), shelf life, HSN codes, and ingredients.
                </p>
              </div>

              <button
                onClick={() => alert('New product recipe creation modal initialized.')}
                className="px-3.5 py-1.5 rounded-xl bg-[#950E09] hover:bg-[#B90505] text-white text-xs font-bold cursor-pointer flex items-center space-x-1.5 shadow"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Add New Delicacy</span>
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead>
                  <tr className="border-b border-[#DDDAC4] bg-[#F7F4D6]/50 text-[#171711]/80 font-bold uppercase tracking-wider text-[11px]">
                    <th className="p-3">Product Name</th>
                    <th className="p-3">Category</th>
                    <th className="p-3">HSN Code</th>
                    <th className="p-3">Shelf Life</th>
                    <th className="p-3">Key Ingredients</th>
                    <th className="p-3">Available Weights</th>
                    <th className="p-3 text-right">Visibility</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#DDDAC4]/60 text-[#171711]">
                  {productsList.map(p => (
                    <tr key={p.id} className="hover:bg-[#F7F4D6]/20 transition-colors">
                      <td className="p-3">
                        <div className="font-bold">{p.name}</div>
                        <div className="text-[10px] text-[#387613] font-medium">{p.tamilName}</div>
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#E09F55]/20 text-[#835926]">
                          {p.category}
                        </span>
                      </td>
                      <td className="p-3 font-mono font-semibold text-gray-700">
                        {p.category === 'sweets' ? '2106 90 99' : '2008 99 99'}
                      </td>
                      <td className="p-3 font-medium text-gray-700">
                        {p.shelfLife}
                      </td>
                      <td className="p-3 text-gray-600 max-w-xs truncate">
                        {p.ingredients?.join(', ') || 'Traditional South Indian recipe'}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {p.variants.map(v => (
                            <span key={v.sku} className="px-1.5 py-0.5 rounded bg-gray-100 text-[9px] font-bold">
                              {v.weight}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                          Active in Store
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* MODULE 6: CUSTOMER & B2B DIRECTORY (SECTION 41) */}
        {/* ============================================================ */}
        {activeTab === 'customers' && (
          <div className="bg-white rounded-2xl border border-[#DDDAC4] shadow-sm overflow-hidden p-5 space-y-4">
            <h3 className="font-display font-bold text-base text-[#171711]">
              Section 41: Customer Accounts & Verified GST Profiles
            </h3>
            <p className="text-xs text-[#171711]/60">
              Manage retail customers, wholesale B2B accounts with GSTINs, and corporate gifting clients.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl border border-[#DDDAC4] bg-[#F7F4D6]/20 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-xs">Tata Consultancy Services</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-bold">Corporate</span>
                </div>
                <div className="text-xs text-gray-600">GSTIN: 33AAACT2800Q1Z8 (Verified)</div>
                <div className="text-[11px] text-gray-500">Contact: Sriram R. • Chennai & Bangalore</div>
                <div className="text-xs font-bold text-[#950E09] pt-1">Past Volume: 320 Luxury Gift Boxes</div>
              </div>

              <div className="p-4 rounded-xl border border-[#DDDAC4] bg-[#F7F4D6]/20 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-xs">Swadeshi Wholesale Mart</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 font-bold">B2B Wholesale</span>
                </div>
                <div className="text-xs text-gray-600">GSTIN: 33AABCS4521E1Z3 (Verified)</div>
                <div className="text-[11px] text-gray-500">Contact: Murugan K. • Madurai Hub</div>
                <div className="text-xs font-bold text-[#950E09] pt-1">Past Volume: 150 Cartons Murukku</div>
              </div>

              <div className="p-4 rounded-xl border border-[#DDDAC4] bg-[#F7F4D6]/20 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-xs">Patel Brothers Supermarkets</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 font-bold">Export Client</span>
                </div>
                <div className="text-xs text-gray-600">US FDA Registered Importer</div>
                <div className="text-[11px] text-gray-500">Contact: Rajiv Patel • New Jersey, USA</div>
                <div className="text-xs font-bold text-[#950E09] pt-1">Past Volume: 500 KG Air Cargo</div>
              </div>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* MODULE 7: EXPORT DESK (SECTION 31 & 46) */}
        {/* ============================================================ */}
        {activeTab === 'export' && (
          <div className="bg-white rounded-2xl border border-[#DDDAC4] shadow-sm overflow-hidden p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display font-bold text-base text-[#171711]">
                  Sections 31 & 46: International Export Operations Desk
                </h3>
                <p className="text-xs text-[#171711]/60">
                  Global diaspora trade, APEDA phytosanitary certificates, and customs documentation.
                </p>
              </div>

              <span className="text-xs bg-[#387613]/10 text-[#387613] font-bold px-3 py-1 rounded-full border border-[#387613]/20">
                APEDA License #APEDA-TN-7829-EXP
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-4 rounded-xl border border-[#DDDAC4] bg-white space-y-1">
                <Globe className="w-5 h-5 text-[#950E09]" />
                <div className="font-bold text-xs">Primary Export Corridors</div>
                <div className="text-[11px] text-gray-600">USA (JFK/EWR), UK (LHR), UAE (DXB), Singapore (SIN), Australia (SYD).</div>
              </div>

              <div className="p-4 rounded-xl border border-[#DDDAC4] bg-white space-y-1">
                <ShieldCheck className="w-5 h-5 text-[#387613]" />
                <div className="font-bold text-xs">Phytosanitary & FDA Conformance</div>
                <div className="text-[11px] text-gray-600">Pre-inspected nitrogen flush packaging with zero insect or aflatoxin contamination.</div>
              </div>

              <div className="p-4 rounded-xl border border-[#DDDAC4] bg-white space-y-1">
                <Truck className="w-5 h-5 text-[#E09F55]" />
                <div className="font-bold text-xs">Freight Rate Contracts</div>
                <div className="text-[11px] text-gray-600">Fixed rate commercial air cargo at ₹240–₹460/kg across airline partners.</div>
              </div>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* MODULE 8: LOGISTICS & LEAD TIME (SECTIONS 33 & 34) */}
        {/* ============================================================ */}
        {activeTab === 'logistics' && (
          <div className="bg-white rounded-2xl border border-[#DDDAC4] shadow-sm overflow-hidden p-5 space-y-4">
            <h3 className="font-display font-bold text-base text-[#171711]">
              Sections 33 & 34: Delivery Calculation & Kitchen Lead Times
            </h3>
            <p className="text-xs text-[#171711]/60">
              Formula: Customer Location + Availability + Quantity + Production Lead Time + Shipping Time = Delivery Date.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
              <div className="p-4 rounded-xl border border-[#DDDAC4] bg-[#F7F4D6]/30 space-y-2">
                <div className="font-bold text-[#950E09]">Local Same-Day (Chennai Metro)</div>
                <p className="text-gray-600">Order cutoff at 2:00 PM IST. Delivery charge: ₹90. Batch cooked fresh daily.</p>
                <div className="text-[11px] font-semibold text-[#387613]">Status: Active & Operating</div>
              </div>

              <div className="p-4 rounded-xl border border-[#DDDAC4] bg-[#F7F4D6]/30 space-y-2">
                <div className="font-bold text-[#950E09]">Standard Domestic Shipping</div>
                <p className="text-gray-600">South India: 2–3 Days. All-India: 3–5 Days. Free delivery above ₹999 (else ₹50).</p>
                <div className="text-[11px] font-semibold text-[#387613]">Status: Active with DTDC/BlueDart</div>
              </div>

              <div className="p-4 rounded-xl border border-[#DDDAC4] bg-[#F7F4D6]/30 space-y-2">
                <div className="font-bold text-[#950E09]">Custom Box / Bulk Orders</div>
                <p className="text-gray-600">Minimum 24h lead time for hand-twisting murukku and hot ghee Mysore Pak preparation.</p>
                <div className="text-[11px] font-semibold text-[#387613]">Status: Lead Time Enforced</div>
              </div>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* MODULE 9: ANALYTICS & REPORTS (SECTION 51) */}
        {/* ============================================================ */}
        {activeTab === 'reports' && (
          <div className="bg-white rounded-2xl border border-[#DDDAC4] shadow-sm overflow-hidden p-5 space-y-4">
            <h3 className="font-display font-bold text-base text-[#171711]">
              Section 51: Business Intelligence & Revenue Reports
            </h3>
            <p className="text-xs text-[#171711]/60">
              Channel conversion rates, top-selling delicacies, and quotation-to-order pipeline.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl border border-[#DDDAC4] space-y-1">
                <span className="text-[11px] text-gray-500 font-semibold block">Quotation Conversion Rate</span>
                <span className="font-display font-black text-2xl text-[#387613]">78.4%</span>
                <p className="text-[11px] text-gray-600">From commercial RFQs into kitchen-confirmed production orders.</p>
              </div>

              <div className="p-4 rounded-xl border border-[#DDDAC4] space-y-1">
                <span className="text-[11px] text-gray-500 font-semibold block">Average Order Value (AOV)</span>
                <span className="font-display font-black text-2xl text-[#950E09]">₹1,840</span>
                <p className="text-[11px] text-gray-600">Driven by multi-pack assortments and wedding gift pre-orders.</p>
              </div>

              <div className="p-4 rounded-xl border border-[#DDDAC4] space-y-1">
                <span className="text-[11px] text-gray-500 font-semibold block">Best-Selling Delicacy</span>
                <span className="font-display font-black text-xl text-[#171711]">Pure Desi Ghee Mysore Pak</span>
                <p className="text-[11px] text-gray-600">Accounting for 34% of total confectionery sales.</p>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
